import { c as _c } from "react/compiler-runtime";
import type { ResourceType } from '@repo/api-gw-sdk';
import { BackupPolicyType, Provider } from '@repo/api-gw-sdk';
import { useMemo } from 'react';
import { apps, sourceAccountId, dataClasses, environment, networkClassification, resourceName, resourceType, sensitivityAnnotations, sourceRegion, subnets, vpc, resourceId, cloudProvider, resourceGroupName, tagKeys, tagKeyValues, sourceAccountTagKeys, sourceAccountTagKeyValues, globalClusterIdentifier, encryptionType, sourceStorageSize } from '@/components/queryBuilder/properties';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import type { FilterProperty } from '@/types/advanceFilter';
const baseProperties: FilterProperty[] = [resourceType, dataClasses, environment, apps];
const propertiesWithSensitivityAnnotations: FilterProperty[] = [...baseProperties, sensitivityAnnotations];
const sourceLocationProperties: FilterProperty[] = [cloudProvider, sourceAccountId, sourceAccountTagKeys, sourceAccountTagKeyValues, sourceRegion, vpc, subnets, resourceGroupName];
const sourceLocationPropertiesWithGlobalClusterIdentifier: FilterProperty[] = [...sourceLocationProperties, globalClusterIdentifier, encryptionType];

// Resource identifiers and resource tags share one divider group: the "Resource
// tags" entry renders as a sub-menu (Tag keys / Tag key-value pairs) right after
// Resource name / Resource ID.
const resourceIdentifierProperties: FilterProperty[] = [resourceName, resourceId, tagKeys, tagKeyValues];
const resourceSizeProperties: FilterProperty[] = [sourceStorageSize];
export const useAllBackupPolicyProperties = () => {
  const $ = _c(5);
  const {
    SensitivityAnnotations: enableSensitivityAnnotations,
    EnableNetworkClassification: enableNetworkClassification
  } = useFeatureFlags();
  const classificationProperties = enableSensitivityAnnotations ? propertiesWithSensitivityAnnotations : baseProperties;
  let t0;
  if ($[0] !== classificationProperties || $[1] !== enableNetworkClassification) {
    t0 = enableNetworkClassification ? [...classificationProperties, networkClassification] : classificationProperties;
    $[0] = classificationProperties;
    $[1] = enableNetworkClassification;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== t0) {
    t1 = [t0, sourceLocationPropertiesWithGlobalClusterIdentifier, resourceIdentifierProperties, resourceSizeProperties];
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
const awsPolicySupportedProperties = new Set<FilterProperty>([resourceType, dataClasses, environment, apps, sensitivityAnnotations, networkClassification, sourceAccountId, sourceRegion, vpc, subnets, resourceName, resourceId, tagKeys, tagKeyValues, globalClusterIdentifier, encryptionType, sourceStorageSize]);
const gcpPolicySupportedProperties = new Set<FilterProperty>([resourceType, dataClasses, environment, apps, sensitivityAnnotations, networkClassification, sourceAccountId, sourceRegion, resourceName, resourceId, tagKeys, tagKeyValues, sourceAccountTagKeys, sourceAccountTagKeyValues, encryptionType, sourceStorageSize]);
const azurePolicySupportedProperties = new Set<FilterProperty>([resourceType, dataClasses, environment, apps, sensitivityAnnotations, networkClassification, sourceAccountId, sourceRegion, resourceGroupName, resourceName, resourceId, tagKeys, tagKeyValues, encryptionType, sourceStorageSize]);
const cloudPropsMap: Record<Provider, Set<FilterProperty>> = {
  [Provider.Aws]: awsPolicySupportedProperties,
  [Provider.Gcp]: gcpPolicySupportedProperties,
  [Provider.Azure]: azurePolicySupportedProperties,
  [Provider.MongoAtlas]: new Set(),
  [Provider.GoogleWorkspace]: new Set(),
  [Provider.Microsoft365]: new Set(),
  [Provider.AzureEntraId]: new Set(),
  [Provider.ProviderUnspecified]: new Set()
};

// Properties not supported by AWS Native Standard resources (EFS, FSX,
// DocumentDB, Neptune, RDS, EKS cluster): these resource types have no application
// detection, no data classification, no Azure MIP sensitivity annotations,
// and no Aurora global cluster identifier when in an AWS_NATIVE_STANDARD policy.
const awsNativeStandardUnsupportedProperties = new Set<FilterProperty>([apps, dataClasses, sensitivityAnnotations, globalClusterIdentifier]);
const azureFilesUnsupportedProperties = new Set<FilterProperty>([apps, dataClasses, sensitivityAnnotations]);

// Azure SQL Database native PITR only enforces the database's Azure short-term
// retention window — Eon does not scan or copy these databases, so there is no
// application detection, data classification, or sensitivity annotation to filter on.
const azureSqlDatabaseNativePitrUnsupportedProperties = new Set<FilterProperty>([apps, dataClasses, sensitivityAnnotations]);
export const useBackupPolicySupportedProperties = (supportedResourceTypes, sourceProviders, policyType) => {
  const $ = _c(9);
  const properties = useAllBackupPolicyProperties();
  let t0;
  bb0: {
    if (!sourceProviders?.length) {
      t0 = properties;
      break bb0;
    }
    let t1;
    if ($[0] !== policyType || $[1] !== properties || $[2] !== sourceProviders || $[3] !== supportedResourceTypes) {
      let t2;
      if ($[5] !== policyType || $[6] !== sourceProviders || $[7] !== supportedResourceTypes) {
        t2 = group => group.filter(prop => {
          if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_STANDARD && awsNativeStandardUnsupportedProperties.has(prop)) {
            return false;
          }
          if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_FILES && azureFilesUnsupportedProperties.has(prop)) {
            return false;
          }
          if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_SQL_DATABASE_NATIVE_PITR && azureSqlDatabaseNativePitrUnsupportedProperties.has(prop)) {
            return false;
          }
          return sourceProviders.some(cloud => {
            if (prop.name === resourceType.name) {
              return supportedResourceTypes.length > 1;
            } else {
              if (prop.name === cloudProvider.name) {
                return sourceProviders.length > 1;
              } else {
                return cloudPropsMap[cloud].has(prop);
              }
            }
          });
        });
        $[5] = policyType;
        $[6] = sourceProviders;
        $[7] = supportedResourceTypes;
        $[8] = t2;
      } else {
        t2 = $[8];
      }
      t1 = properties.map(t2);
      $[0] = policyType;
      $[1] = properties;
      $[2] = sourceProviders;
      $[3] = supportedResourceTypes;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    t0 = t1;
  }
  return t0;
};